import { Activity, type Exercise } from "@/Common/ActivityFunctions";
import { debouncer, getDiamondFromDifficulty } from "@/Util/utils";
import MiniSearch from 'minisearch';

export const sortOptions = [
  { label: 'Recommended', value: 'recommended' },
  { label: 'Relevance', value: 'search' },
  { label: 'Name', value: 'name' },
  { label: 'XSS', value: 'xss' },
  { label: 'Duration', value: 'duration' },
  { label: 'Difficulty', value: 'difficulty' }
];
export const sortDirOptions = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' }
];

export const specificityOptions = [
  { label: 'All', value: 'all'},
  { label: 'Pure', value: 'pure'},
  { label: 'Mixed', value: 'mixed'},
  { label: 'Polar', value: 'polar'},
]

export const focusOptions = [
  { label: 'All', value: 'all'},
  { label: 'Power Sprinter', value: 'power sprinter'},
  { label: 'Road Sprinter', value: 'road sprinter'},
  { label: 'Pursuiter', value: 'pursuiter'},
  { label: 'Puncheur', value: 'puncheur'},
  { label: 'Breakaway Specialist', value: 'breakaway specialist'},
  { label: 'Rouleur', value: 'rouleur'},
  { label: 'GC Specialist', value: 'gc specialist'},
  { label: 'Climber', value: 'climber'},
  { label: 'Endurance', value: 'endurance'},
];

export const difficultyOptions = [
  { label: '-', value: null, },
  { label: '◆', value: 1, },
  { label: '◆⬖', value: 1.5, },
  { label: '◆◆', value: 2, },
  { label: '◆◆⬖', value: 2.5, },
  { label: '◆◆◆', value: 3, },
  { label: '◆◆◆⬖', value: 3.5, },
  { label: '◆◆◆◆', value: 4, },
  { label: '◆◆◆◆⬖', value: 4.5, },
  { label: '◆◆◆◆◆', value: 5, },
]

export const trainingTypeOptions = [
  { label: 'All', value: 'all', exerciseType: 'All', venues: [], },
  { label: 'Workouts', value: 'workouts', exerciseType: 'Workout', venues: [] },
  { label: 'Outdoor', value: 'outdoor', exerciseType: 'Activity', venues: ['outdoor'] },
  { label: 'Virtual', value: 'virtual', exerciseType: 'Activity', venues: ['virtual', 'indoor'] },
  { label: 'Sessions', value: 'sessions', exerciseType: 'Session', venues: [] },
];

const filterDebouncer = debouncer(400);

export class SearchFilterFieldsAndData {
  originalExercises: Exercise[];
  onFilterChange: (filteredExercises: Exercise[]) => void;
  onFilterFieldChange: () => void; // Necessary for reactivity

  constructor(originalExercises: Exercise[], onFilterChange: (filteredExercises: Exercise[]) => void, onFilterFieldChange: () => void) {
    this.originalExercises = originalExercises;
    this.filteredExercises = [...originalExercises];
    this.onFilterChange = onFilterChange;
    this.onFilterFieldChange = onFilterFieldChange;
    this.afterSearchExercises = [...originalExercises];
    this.exerciseMap = new Map(originalExercises.map(e => [e.path, e]));
    this.originalOrder = new Map(originalExercises.map((e, i) => [e.path, i]));

    this.mini = new MiniSearch({
      fields: ['name','suitability','description'],
      storeFields: ['path'],
      idField: 'path',
      searchOptions: {
        boost: { name: 5, suitability: 3, description: 1 },
        prefix: true,
        fuzzy: 0.2
      }
    });
    this.mini.addAll(originalExercises);

    onFilterChange(this.filteredExercises)
  }

  executeFilterDebouncer = () => filterDebouncer.debounce(()=>this.runSearch());

  private filteredExercises: Exercise[];
  private afterSearchExercises: Exercise[];
  private prevSearchTerm: string = "";
  searchTerm: string = "";

  // Sorting
  sortBy = sortOptions[0];
  sortDirection = sortDirOptions[1];
  private exerciseMap: Map<string, Exercise>; // path -> exercise
  private originalOrder: Map<string, number>; // path -> index for recommended order
  private searchScores = new Map<string, number>(); // path -> last search score

  // Numeric filter bounds (undefined = no constraint)
  minDuration: number | null = null; maxDuration: number | null = null;
  minXss: number | null = null; maxXss: number | null = null;
  minXlss: number | null = null; maxXlss: number | null = null;
  minXhss: number | null = null; maxXhss: number | null = null;
  minXpss: number | null = null; maxXpss: number | null = null;
  minDifficulty = difficultyOptions[0]; maxDifficulty = difficultyOptions[0];

  // Categorical filters
  selectedSpecificity = specificityOptions[0];
  selectedFocus = focusOptions[0];
  trainingType = trainingTypeOptions[0];

  // Boolean filters
  isLiked: boolean = false; isDisliked: boolean = false;
  showStandard: boolean = true;
  showCommunity: boolean = true;
  showPersonal: boolean = true;

  private mini: MiniSearch;

  runSearch = () => {
    const q = this.searchTerm.trim();
    // Base set (search or all)
    if (q != this.prevSearchTerm) {
      if (!this.prevSearchTerm || this.prevSearchTerm === "") {
        this.sortBy = sortOptions[1];
        this.sortDirection = sortDirOptions[1];
        this.onFilterFieldChange();
      } else if (!q || q === "") {
        this.sortBy = sortOptions[0];
        this.sortDirection = sortDirOptions[1];
        this.onFilterFieldChange();
      }
      this.prevSearchTerm = q;
      if (!q) {
        this.afterSearchExercises = [...this.originalExercises];
        this.searchScores.clear();
      } else {
        const miniSearchResult = this.mini.search(q);
        this.searchScores = new Map(miniSearchResult.map(r => [r.path, (r as any).score ?? 0]));
        this.afterSearchExercises = miniSearchResult.map(e => this.exerciseMap.get(e.path)).filter(e => e !== undefined);
      }
    }

    // Numeric filtering helper
    const inRange = (val: number | undefined | null, min: number | null, max: number | null) => {
      if (val == null) val = 0; // treat missing as 0
      if (min != null && val < min) return false;
      if (max != null && val > max) return false;
      return true;
    };

    // Numeric filters
    this.filteredExercises = this.afterSearchExercises.filter(ex => {
      return (
        inRange((ex.duration ?? 0) / 60, this.minDuration, this.maxDuration) &&
        inRange(ex.xss, this.minXss, this.maxXss) &&
        inRange(ex.xlss, this.minXlss, this.maxXlss) &&
        inRange(ex.xhss, this.minXhss, this.maxXhss) &&
        inRange(ex.xpss, this.minXpss, this.maxXpss) &&
        inRange(getDiamondFromDifficulty(ex.difficulty ?? 0), this.minDifficulty.value, this.maxDifficulty.value)
      );
    });

    // Boolean and categorical filters
    this.filteredExercises = this.filteredExercises.filter(ex => {
      if (this.selectedSpecificity.value !== "all") {
        if (Activity.getSpecRating(ex).toLowerCase() !== this.selectedSpecificity.value) return false;
      }
      if (this.selectedFocus.value !== "all") {
        if ((ex.focus || '').toLowerCase() !== this.selectedFocus.value) return false;
      }
      if (this.trainingType.value !== 'all') {
        if (this.trainingType.exerciseType && ex.exerciseType !== this.trainingType.exerciseType) return false;
        if (this.trainingType.venues.length > 0 && !this.trainingType.venues.includes(ex.venue || '')) return false;
      } else {
        // Exclude sessions from All, only show if explicitly selected
        if (ex.exerciseType === 'sessions') return false;
      }

      if (this.isLiked || this.isDisliked) {
        if (this.isLiked && (ex.liked || ex.favorite)) return true;
        if (this.isDisliked && ex.disliked) return true;
        return false;
      }

      let selectedWorkoutTypes = {
        'Admin': this.showStandard,
        'Coach': this.showCommunity,
        'User': this.showPersonal
      };

      return selectedWorkoutTypes[ex.owner || 'Admin'];
    });

    this.applySort();
  }

  applySort = () => {
    const dir = this.sortDirection.value === 'asc' ? 1 : -1;
    const getVal = (ex: Exercise) => {
      switch (this.sortBy.value) {
        case 'name': return (ex.name || '').toLowerCase();
        case 'xss': return ex.xss ?? -Infinity;
        case 'duration': return ex.duration ?? -Infinity;
        case 'difficulty': return ex.difficulty ?? -Infinity;
        case 'search': {
          if (!this.searchTerm.trim()) return this.originalOrder.get(ex.path) ?? Infinity; // fallback to recommended
          // MiniSearch lower score = more relevant (score is a similarity distance). Invert so higher relevance sorts first.
          const s = this.searchScores.get(ex.path) ?? 1; // missing -> worst
          return s;
        }
        case 'recommended':
        default:
          return -(this.originalOrder.get(ex.path) ?? Infinity);
      }
    };
    this.filteredExercises = [...this.filteredExercises].sort((a, b) => {
      if (!a?.path || !b?.path) return 0;
      let va = getVal(a);
      let vb = getVal(b);
      // Strings vs numbers
      if (typeof va === 'string' && typeof vb === 'string') {
        const cmp = va.localeCompare(vb);
        if (cmp !== 0) return cmp * dir;
      } else {
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
      }
      // Tie-breakers: original order, then path
      const ra = this.originalOrder.get(a.path) ?? 0;
      const rb = this.originalOrder.get(b.path) ?? 0;
      if (ra !== rb) return (ra - rb) * dir;
      return (a.path.localeCompare(b.path)) * dir;
    });
    this.onFilterChange(this.filteredExercises);
  }

  onSortByChange = (v) => {
    this.sortBy = v;
    this.applySort();
  }

  onSortDirectionChange = (v) => {
    this.sortDirection = v;
    this.applySort();
  }

  onNumericFilterChange = () => {
    // Coerce empty strings to null to indicate no filter
    const normalize = (v: any) => (v === '' || v == null ? null : isNaN(Number(v)) ? null : Number(v));
    this.minDuration = normalize(this.minDuration);
    this.maxDuration = normalize(this.maxDuration);
    this.minXss = normalize(this.minXss); this.maxXss = normalize(this.maxXss);
    this.minXlss = normalize(this.minXlss); this.maxXlss = normalize(this.maxXlss);
    this.minXhss = normalize(this.minXhss); this.maxXhss = normalize(this.maxXhss);
    this.minXpss = normalize(this.minXpss); this.maxXpss = normalize(this.maxXpss);
    this.executeFilterDebouncer();
  }

  onBooleanOrCategoricalFilterChange = () => {
    this.executeFilterDebouncer();
  }

  resetFilters = () => {
    this.searchTerm = "";
    this.minDuration = null; this.maxDuration = null;
    this.minXss = null; this.maxXss = null;
    this.minXlss = null; this.maxXlss = null;
    this.minXhss = null; this.maxXhss = null;
    this.minXpss = null; this.maxXpss = null;
    this.minDifficulty = difficultyOptions[0]; this.maxDifficulty = difficultyOptions[0];
    this.sortBy = sortOptions[0];
    this.sortDirection = sortDirOptions[1];
    this.selectedFocus = focusOptions[0];
    this.selectedSpecificity = specificityOptions[0];
    this.trainingType = trainingTypeOptions[0];
    this.isLiked = false;
    this.isDisliked = false;
    this.showStandard = true; this.showCommunity = true; this.showPersonal = true;
    this.runSearch();
  }

  isFilterApplied = (filterName: 'searchTerm' | 'minDuration' | 'maxDuration' | 'minXss' | 'maxXss' | 'minXlss' | 'maxXlss' | 'minXhss' | 'maxXhss' | 'minXpss' | 'maxXpss' | 'minDifficulty' | 'maxDifficulty' | 'sortBy' | 'sortDirection' | 'selectedFocus' | 'selectedSpecificity' | 'trainingType' | 'isLiked' | 'isDisliked' | 'showStandard' | 'showCommunity' | 'showPersonal' = null) => {
    switch (filterName) {
      case 'searchTerm': return this.searchTerm.trim() !== "";
      case 'minDuration': return this.minDuration != null;
      case 'maxDuration': return this.maxDuration != null;
      case 'minXss': return this.minXss != null;
      case 'maxXss': return this.maxXss != null;
      case 'minXlss': return this.minXlss != null;
      case 'maxXlss': return this.maxXlss != null;
      case 'minXhss': return this.minXhss != null;
      case 'maxXhss': return this.maxXhss != null;
      case 'minXpss': return this.minXpss != null;
      case 'maxXpss': return this.maxXpss != null;
      case 'minDifficulty': return this.minDifficulty.value !== difficultyOptions[0].value;
      case 'maxDifficulty': return this.maxDifficulty.value !== difficultyOptions[0].value;
      case 'sortBy': return this.sortBy.value !== sortOptions[0].value;
      case 'sortDirection': return this.sortDirection.value !== sortDirOptions[1].value;
      case 'selectedFocus': return this.selectedFocus.value !== focusOptions[0].value;
      case 'selectedSpecificity': return this.selectedSpecificity.value !== specificityOptions[0].value;
      case 'trainingType': return this.trainingType.value !== trainingTypeOptions[0].value;
      case 'isLiked': return this.isLiked;
      case 'isDisliked': return this.isDisliked;
      case 'showStandard': return !this.showStandard;
      case 'showCommunity': return !this.showCommunity;
      case 'showPersonal': return !this.showPersonal;
      default:
        return (
          this.searchTerm.trim() !== "" ||
          this.minDuration != null || this.maxDuration != null ||
          this.minXss != null || this.maxXss != null ||
          this.minXlss != null || this.maxXlss != null ||
          this.minXhss != null || this.maxXhss != null ||
          this.minXpss != null || this.maxXpss != null ||
          this.minDifficulty.value !== difficultyOptions[0].value || this.maxDifficulty.value !== difficultyOptions[0].value ||
          this.sortBy.value !== sortOptions[0].value ||
          this.sortDirection.value !== sortDirOptions[1].value ||
          this.selectedFocus.value !== focusOptions[0].value ||
          this.selectedSpecificity.value !== specificityOptions[0].value ||
          this.trainingType.value !== trainingTypeOptions[0].value ||
          this.isLiked || this.isDisliked ||
          !this.showStandard || !this.showCommunity || !this.showPersonal
        );
    }
  }
}